import { Check, Star, Chrome } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CHROME_WEB_STORE_URL } from "@/config/extension";

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "/month",
    description: "Perfect for individuals and small families getting started",
    features: [
      "Chrome Extension included",
      "Basic content filtering",
      "Up to 3 devices",
      "Standard support",
      "Basic threat protection",
      "Community forum access",
    ],
    cta: "Get Started Free",
    href: "/auth",
    popular: false,
  },
  {
    name: "Pro",
    price: "$9.99",
    period: "/month",
    description: "Ideal for growing families and small teams",
    features: [
      "Advanced Chrome Extension features",
      "Smart keyword blocking",
      "Scheduled productivity sessions",
      "Advanced content filtering",
      "Up to 10 devices",
      "Priority support",
      "Advanced threat protection",
      "Custom content policies",
      "Detailed analytics",
      "Family management tools",
    ],
    cta: "Start Pro Trial",
    href: "/auth",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "$29.99",
    period: "/month",
    description: "For organizations and large families",
    features: [
      "Enterprise Chrome Extension features",
      "Advanced keyword intelligence",
      "Complex scheduling & automation",
      "Enterprise-grade filtering",
      "Unlimited devices",
      "24/7 priority support",
      "Advanced threat intelligence",
      "Custom integrations",
      "Advanced reporting",
      "Team management",
      "API access",
      "White-label options",
    ],
    cta: "Contact Sales",
    href: "/auth",
    popular: false,
  },
];

export default function PricingSection() {
  return (
    <section className="py-16 md:py-32 bg-muted/50" id="pricing">
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-semibold mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your needs. All plans include our Chrome
            Extension and DNS filtering technology with no hidden fees or
            surprises. Start protecting your family in under 2 minutes!
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`relative p-6 ${
                plan.popular ? "ring-2 ring-primary shadow-lg scale-105" : ""
              }`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground">
                  Most Popular
                </Badge>
              )}

              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl mb-2">{plan.name}</CardTitle>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {plan.description}
                </p>
              </CardHeader>

              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  asChild
                  className={`w-full ${
                    plan.popular ? "bg-primary" : "bg-secondary"
                  }`}
                >
                  <a href={plan.href}>{plan.cta}</a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-muted-foreground mb-4">
            All plans include a 14-day free trial. No credit card required to
            start.
          </p>

          {/* Install Extension CTA */}
          <div className="mb-6">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-[#00F5A0] to-[#0AEFFF] text-[#0A0F0D] font-semibold hover:opacity-90"
            >
              <a
                href={CHROME_WEB_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <Chrome className="w-5 h-5" />
                Install Chrome Extension (Free)
              </a>
            </Button>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            </div>
            <span>Trusted by 10,000+ users worldwide</span>
          </div>
        </div>
      </div>
    </section>
  );
}
